import type { OrderSummary, OrdersOverview } from '@/api/orders';
import type { DraftSummary } from '@/api/orderPrep';
import { STAGE_TITLES, stageOf, type Stage } from '@/features/orders/lib';
import type { StatusTone } from '@/lib/status';
import { addDays, localDay, type CalendarItem } from './calendarItems';

// ---------------------------------------------------------------------------
// The stage table: one row per stage, one meaning per column. Pure functions
// over the same payloads the queues read, so a count here is always the count
// the queue would show. Every cell is one figure and at most one qualifier;
// a stage with nothing to say in a column says nothing, and the page prints
// a dash.
// ---------------------------------------------------------------------------

export const WINDOW_DAYS = 30;


export interface Fact {
  text: string;
  /** Rendered as a StatusPill; untoned facts are neutral. */
  tone?: StatusTone;
}

export interface CurrencyTotal {
  currency: string;
  amount: number;
}

export interface StageValue {
  /** USD. Null when nothing in the stage carries a price yet. */
  amount: number | null;
  /** True when any part is an estimate — the figure prints with a tilde. */
  approx: boolean;
  /** What the money is: "planned", "committed", "awaiting balance". */
  label: string;
  /** What it is made of: "17 drafts · 51 lines unsized". */
  sub: string | null;
  /**
   * The same money in every other currency the stage carries, one total per
   * currency, largest first. Never added to `amount` — a figure in two
   * currencies is no figure — but printed beside it so the cell shows all
   * the money instead of a note about the part it left out.
   */
  others: CurrencyTotal[];
}

export interface StageRow {
  stage: Stage;
  title: string;
  to: string;
  count: number;
  /** One qualifier under the count: "15 undated", "34 parcels in transit". */
  countSub: string | null;
  /** Null when the caller may not read costs. */
  value: StageValue | null;
  /** What needs a person now, in tone. */
  attention: Fact[];
  /** What moved through the stage in the last WINDOW_DAYS days. */
  recent: { text: string; money: number | null } | null;
}

/** "1 parcel" / "3 parcels". */
function plural(count: number, singular: string, pluralForm = `${singular}s`): string {
  return `${count.toLocaleString()} ${count === 1 ? singular : pluralForm}`;
}

function facts(entries: Array<Fact | false | null | undefined>): Fact[] {
  return entries.filter((f): f is Fact => Boolean(f));
}

/** "a · b", skipping what is empty; null when nothing is left. */
function join(parts: Array<string | false | null | undefined>): string | null {
  const list = parts.filter((p): p is string => Boolean(p));
  return list.length ? list.join(' · ') : null;
}

/** A parcel that is somewhere between the vendor's dock and ours. */
const MOVING = new Set(['in_transit', 'out_for_delivery', 'available_for_pickup']);

/**
 * A draft the run has finished with and nothing flagged: sized, reviewed
 * without a question, not blocked. The To-order queue's own "ready" chip
 * applies the same test.
 */
export function draftReady(d: DraftSummary): boolean {
  return ['active', 'final'].includes(d.status)
    && !d.blockedReason
    && d.review?.verdict !== 'flag'
    && Object.values(d.counts).every((c) => !c.unsized && !c.flagged);
}

/** Is `value` a day inside the last WINDOW_DAYS days, today included? */
function windowTest(today: string) {
  const since = addDays(today, -WINDOW_DAYS);
  return (value: string | null | undefined) => {
    const day = localDay(value);
    return day != null && day >= since && day <= today;
  };
}

/**
 * A money field summed across rows, one total per currency. USD is the
 * headline; every other currency is totalled on its own and never added to it.
 * Rows with no price are counted so the cell can say how much it cannot see.
 */
function usdSum<T extends { currency: string }>(
  rows: T[],
  value: (row: T) => number | null | undefined
): { total: number; priced: number; unpriced: number; others: CurrencyTotal[] } {
  let total = 0;
  let priced = 0;
  let unpriced = 0;
  const others = new Map<string, number>();
  for (const row of rows) {
    const v = value(row);
    if (v == null) { unpriced += 1; continue; }
    if (row.currency !== 'USD') { others.set(row.currency, (others.get(row.currency) ?? 0) + v); continue; }
    total += v;
    priced += 1;
  }
  return { total, priced, unpriced, others: currencyTotals(others) };
}

/** A currency map as a list, biggest total first. */
function currencyTotals(map: Map<string, number>): CurrencyTotal[] {
  return [...map]
    .map(([currency, amount]) => ({ currency, amount }))
    .sort((a, b) => b.amount - a.amount);
}

export function stageRows(input: {
  orders: OrderSummary[];
  overview: OrdersOverview;
  items: CalendarItem[];
  /** Null when the user cannot read drafts, or the read failed. */
  drafts: DraftSummary[] | null;
  today: string;
  /** False for callers without the costs read: no value column, no money anywhere. */
  withMoney: boolean;
}): StageRow[] {
  const { orders, overview, items, drafts, today, withMoney } = input;
  const active = orders.filter((o) => !o.archivedAt);
  const inWindow = windowTest(today);
  const weekEnd = addDays(today, 6);
  const thisWeek = (day: string | null | undefined) => day != null && day.slice(0, 10) >= today && day.slice(0, 10) <= weekEnd;
  const byStage = (stage: Stage) => active.filter((o) => stageOf(o) === stage);
  const value = (
    amount: number | null, approx: boolean, label: string, sub: string | null, others: CurrencyTotal[] = []
  ): StageValue | null => (withMoney ? { amount, approx, label, sub, others } : null);
  const row = (stage: Stage, fields: Omit<StageRow, 'stage' | 'title' | 'to'>): StageRow =>
    ({ stage, title: STAGE_TITLES[stage], to: `/journeys?stage=${stage}`, ...fields });

  // Committed money: the invoice total where one exists, else the priced
  // order lines. A journey with neither is counted as unpriced, not as zero.
  const committed = (o: OrderSummary) => o.invoicedTotal ?? o.orderedTotal ?? null;
  const committedSub = (sum: ReturnType<typeof usdSum>) =>
    join([sum.unpriced > 0 && `${sum.unpriced} unpriced`]);

  // ---- To order -----------------------------------------------------------
  const toOrder = byStage('to_order');
  const overdue = toOrder.filter((o) => o.orderDueDate && o.orderDueDate.slice(0, 10) < today).length;
  const dueWeek = toOrder.filter((o) => thisWeek(o.orderDueDate)).length;
  const undated = toOrder.filter((o) => !o.orderDueDate).length;
  const draftsReady = drafts
    ? drafts.filter((d) => draftReady(d) && toOrder.some((o) => o.id === d.orderId)).length
    : 0;
  const announced30 = active.filter((o) => inWindow(o.createdAt)).length;
  // Planned: every open draft on a journey still to order, USD journeys only.
  const currencyOf = new Map(toOrder.map((o) => [o.id, o.currency]));
  const planned = (drafts ?? []).filter((d) => currencyOf.has(d.orderId) && d.status !== 'closed');
  let plannedTotal = 0;
  let plannedPriced = 0;
  let plannedApprox = false;
  let unsized = 0;
  const plannedOthers = new Map<string, number>();
  for (const d of planned) {
    const currency = currencyOf.get(d.orderId) ?? 'USD';
    for (const c of Object.values(d.counts)) {
      // Unsized is a count of lines, not money: it holds in any currency.
      unsized += c.unsized;
      if (c.estCost == null) continue;
      if (currency !== 'USD') { plannedOthers.set(currency, (plannedOthers.get(currency) ?? 0) + c.estCost); continue; }
      plannedTotal += c.estCost;
      plannedPriced += 1;
      if (c.costEstimated) plannedApprox = true;
    }
  }

  // ---- On order -----------------------------------------------------------
  const onOrder = byStage('on_order');
  const placed30 = active.filter((o) => inWindow(o.placedDate)).length;
  const shipsWeek = onOrder.filter((o) => thisWeek(o.expectedAvailability)).length;
  const setup = onOrder.filter((o) => o.needsSetup || o.newSupplierNeeded).length;
  const onOrderSum = usdSum(onOrder, committed);

  // ---- On the way ---------------------------------------------------------
  const inTransit = byStage('in_transit');
  const moving = active.reduce((n, o) => n + o.packages.filter((p) => MOVING.has(p.status)).length, 0)
    + (overview.unlinkedPackages ?? []).filter((p) => MOVING.has(p.status)).length;
  const arrivingWeek = items
    .filter((i) => i.kind === 'arriving' && !i.overdue && thisWeek(i.day))
    .reduce((n, i) => n + i.count, 0);
  const delivered30 = items
    .filter((i) => i.kind === 'delivered' && inWindow(i.day))
    .reduce((n, i) => n + i.count, 0);
  const problems = overview.work.packageProblems;
  const inTransitSum = usdSum(inTransit, committed);

  // ---- Arrived ------------------------------------------------------------
  const arrived = byStage('arrived');
  const unbalanced = overview.receivings.filter((r) => !r.balancedAt);
  const ready = unbalanced.filter((r) => r.ready && !r.blockers.length).length;
  const blocked = unbalanced.length - ready;
  const backlog = usdSum(unbalanced, (r) => r.invoiceTotal ?? r.bookedTotal);
  const bookedRows = overview.receivings.filter((r) => inWindow(r.dateCreated));
  const booked = usdSum(bookedRows, (r) => r.bookedTotal);

  // ---- Done ---------------------------------------------------------------
  const done = byStage('done');
  const balancedRows = overview.receivings.filter((r) => inWindow(r.balancedAt));
  const balanced = usdSum(balancedRows, (r) => r.targetTotal ?? r.invoiceTotal ?? r.bookedTotal);

  return [
    row('to_order', {
      count: toOrder.length,
      countSub: undated > 0 ? `${undated} undated` : null,
      value: value(plannedPriced > 0 ? plannedTotal : null, plannedApprox || unsized > 0, 'planned', join([
        drafts != null && planned.length > 0 && plural(planned.length, 'draft'),
        unsized > 0 && `${plural(unsized, 'line')} unsized`,
      ]), currencyTotals(plannedOthers)),
      attention: facts([
        overdue > 0 && { text: `${overdue} overdue`, tone: 'danger' },
        dueWeek > 0 && { text: `${dueWeek} due this week`, tone: 'warning' },
        draftsReady > 0 && { text: `${plural(draftsReady, 'draft')} ready`, tone: 'success' },
      ]),
      recent: announced30 > 0 ? { text: `${announced30} announced`, money: null } : null,
    }),
    row('on_order', {
      count: onOrder.length,
      countSub: shipsWeek > 0 ? `${shipsWeek} ship${shipsWeek === 1 ? 's' : ''} this week` : null,
      value: value(onOrderSum.priced > 0 ? onOrderSum.total : null, false, 'committed', committedSub(onOrderSum), onOrderSum.others),
      attention: facts([
        setup > 0 && { text: `${setup} need${setup === 1 ? 's' : ''} setup`, tone: 'warning' },
      ]),
      recent: placed30 > 0 ? { text: `${placed30} placed`, money: null } : null,
    }),
    row('in_transit', {
      count: inTransit.length,
      countSub: moving > 0 ? `${plural(moving, 'parcel')} in transit` : null,
      value: value(inTransitSum.priced > 0 ? inTransitSum.total : null, false, 'in transit', committedSub(inTransitSum), inTransitSum.others),
      // Arrivals are a heads-up for the warehouse rather than a problem, so
      // they sit beside the problems without a tone.
      attention: facts([
        problems > 0 && { text: plural(problems, 'delivery problem'), tone: 'danger' },
        arrivingWeek > 0 && { text: `${plural(arrivingWeek, 'parcel')} arriving this week` },
      ]),
      recent: delivered30 > 0 ? { text: `${plural(delivered30, 'parcel')} delivered`, money: null } : null,
    }),
    row('arrived', {
      count: arrived.length,
      countSub: null,
      value: value(backlog.priced > 0 ? backlog.total : null, false, 'to balance', join([
        unbalanced.length > 0 && plural(unbalanced.length, 'receiving'),
        blocked > 0 && `${blocked} blocked`,
      ]), backlog.others),
      attention: facts([
        ready > 0 && { text: `${ready} ready to balance`, tone: 'success' },
      ]),
      recent: bookedRows.length > 0
        ? { text: `${plural(bookedRows.length, 'receiving')} booked`, money: withMoney && booked.priced > 0 ? booked.total : null }
        : null,
    }),
    row('done', {
      count: done.length,
      countSub: null,
      value: value(null, false, '', null),
      attention: [],
      recent: balancedRows.length > 0
        ? { text: `${balancedRows.length} balanced`, money: withMoney && balanced.priced > 0 ? balanced.total : null }
        : null,
    }),
  ];
}

/** "$308K", "€4.2K" — a headline figure, not a ledger entry. */
export function moneyCompact(value: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency, notation: 'compact', maximumFractionDigits: 1,
  }).format(value);
}
